import type { CollectionKey } from "../../netlify/functions/_shared/types";

const getHeaders = () => {
  const token = sessionStorage.getItem("admin_token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const handleResponse = async (res: Response) => {
  if (res.status === 401) {
    sessionStorage.removeItem("admin_token");
    window.location.href = "/admin/login";
    throw new Error("Unauthorized");
  }
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "API Request Failed");
  }
  return data;
};

export const adminExport = async () => {
  const res = await fetch("/api/admin/export", { headers: getHeaders() });
  if (res.status === 401) {
    sessionStorage.removeItem("admin_token");
    window.location.href = "/admin/login";
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `festival-data-${new Date().toISOString().split("T")[0]}.json`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const adminExportCollection = async (collection: CollectionKey) => {
  const res = await fetch(`/api/admin/export?collection=${collection}`, { headers: getHeaders() });
  if (res.status === 401) {
    sessionStorage.removeItem("admin_token");
    window.location.href = "/admin/login";
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error(`Export of ${collection} failed`);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${collection}-${new Date().toISOString().split("T")[0]}.json`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const adminImport = async (bundle: any) => {
  const res = await fetch("/api/admin/import", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(bundle),
  });
  return handleResponse(res);
};

export const adminUpdateCollection = async (collection: CollectionKey, data: any) => {
  const res = await fetch(`/api/admin/${collection}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
};

export const adminCreateEntity = async (collection: CollectionKey, entity: any) => {
  const res = await fetch(`/api/admin/${collection}`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(entity),
  });
  return handleResponse(res);
};

export const adminUpdateEntity = async (collection: CollectionKey, id: string, entity: any) => {
  const res = await fetch(`/api/admin/${collection}/${id}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(entity),
  });
  return handleResponse(res);
};

export const adminDeleteEntity = async (collection: CollectionKey, id: string) => {
  const res = await fetch(`/api/admin/${collection}/${id}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const adminSeed = async () => {
  const res = await fetch("/api/seed", {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const adminVerifySecret = async (token: string): Promise<boolean> => {
  try {
    const res = await fetch("/api/admin/verify", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return res.status !== 401;
  } catch (e) {
    return false;
  }
};

export const adminGetNotification = async (): Promise<{
  title?: string;
  message: string;
  createdAt?: string;
  validityHours?: number;
  validityMinutes?: number;
  validUntil?: string;
}> => {
  const res = await fetch("/api/admin/notification", { headers: getHeaders() });
  return handleResponse(res);
};

export const adminSetNotification = async (
  title: string,
  message: string,
  validityHours: number,
  validityMinutes: number,
): Promise<{ success: boolean; message: string }> => {
  const res = await fetch("/api/admin/notification", {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ title, message, validityHours, validityMinutes }),
  });
  return handleResponse(res);
};
